import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { createForm, formShape } from 'rc-form';
import TextField from '../../SolvPatternLibrary/TextField';
import FormLabelControl from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button, { ButtonVariations } from '../../SolvPatternLibrary/Button';
import { colors } from '../../../constants/colors';
import { fontSize, fontFamily } from '../../../constants/text';
import { biggerThanOrEqualTo } from '../../../core/util/styledComponents';
import ConsentLabel from './ConsentLabel';
import {
  BUTTON_STATUS_ERROR,
  BUTTON_STATUS_INITIAL,
  BUTTON_STATUS_LOADING,
  BUTTON_STATUS_SUCCESS,
} from '../../SolvPatternLibrary/Button/TransitionButton';
import { PRIMARY_RATING } from './PrimaryRatingSelector';

export const RATING_DESCRIPTION_FIELD = 'ratingDescription';
export const CONSENT_FIELD = 'consentCheckbox';

const Divider = styled.div`
  height: 1px;
  background-color: ${colors.dorianGrey};
  display: block;
  margin: 0 20px;

  ${biggerThanOrEqualTo.sm`
    width: 100%;
    margin: 0 auto;
  `}
`;

const Root = styled.form`
  padding: 26px 20px;
`;

const Title = styled.h1`
  margin: 0 auto 20px;
  display: block;
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.large};
  color: ${colors.blueMonday};
`;

const DescriptionField = styled(TextField)`
  max-width: 500px;
  margin: 0 auto;
  background: ${colors.theWhiteAlbum};
  overflow: hidden;

  & .details-root {
    height: 90px;
  }

  & .input-root {
    height: 90px !important;
    text-indent: 0;
    padding: 12px 16px;
  }
`;

const ConsentContainer = styled(FormLabelControl)`
  max-width: 500px;
  margin: 0 auto;
  padding: 26px 0;
`;

const ConsentBox = styled(Checkbox)`
  display: inline-block;
  padding: 2px 8px 0 0;
  flex-grow: 0;
  align-self: normal;
`;

const StyledConsentLabel = styled(ConsentLabel)`
  flex-grow: 1;
  cursor: pointer;
  font-size: ${fontSize.mediumSmall};
  font-family: ${fontFamily.book};
  color: ${colors.blueMonday};
  display: inline-block;
  white-space: normal;
  text-align: left;
`;

const SubmitButton = styled(Button)`
  max-width: 500px;
  margin: 0 auto;
`;

class RatingDescriptionForm extends PureComponent {
  static propTypes = {
    form: formShape.isRequired,
    submit: PropTypes.func.isRequired,
    leaveAReview: PropTypes.object,
    location: PropTypes.object,
  };

  state = { isSubmitting: false };

  getButtonStatus = () => {
    const { isSubmitting } = this.state;
    const { leaveAReview } = this.props;

    switch (leaveAReview.submissionStatus) {
      default:
      case undefined:
        return BUTTON_STATUS_INITIAL;
      case 'error':
        isSubmitting && this.setState({ isSubmitting: false });
        return BUTTON_STATUS_ERROR;
      case 'submitting':
        return BUTTON_STATUS_LOADING;
      case 'success':
        return BUTTON_STATUS_SUCCESS;
    }
  };

  descriptionProps = {
    ...this.props.form.getFieldProps(RATING_DESCRIPTION_FIELD),
    id: RATING_DESCRIPTION_FIELD,
    placeholder: 'Comments',
    onChange: ({ target: { id, value } }) => this.props.form.setFieldsValue({ [id]: value }),
  };

  consentProps = {
    ...this.props.form.getFieldProps(CONSENT_FIELD),
    id: CONSENT_FIELD,
    onChange: (_, checked) => this.props.form.setFieldsValue({ [CONSENT_FIELD]: checked }),
  };

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    const { isSubmitting } = this.state;
    if (isSubmitting) return;

    const { form, submit } = this.props;

    this.setState({ isSubmitting: true });
    submit(form.getFieldsValue());
  };

  render() {
    const {
      leaveAReview,
      location: { displayNamePrimary },
    } = this.props;

    return (
      <Fragment>
        <Divider />
        <Root onSubmit={this.handleSubmit}>
          <Title>Describe your experience.</Title>
          <DescriptionField
            multiline
            fullWidth
            InputProps={{ classes: { root: 'details-root' } }}
            classes={{ inputInput: 'input-root' }}
            {...this.descriptionProps}
          />
          <ConsentContainer
            control={<ConsentBox color="primary" {...this.consentProps} />}
            label={<StyledConsentLabel displayNamePrimary={displayNamePrimary} />}
          />
          <SubmitButton
            type="submit"
            data-testid="leave-a-review-submit"
            animationEnabled
            status={this.getButtonStatus()}
            disabled={!leaveAReview[PRIMARY_RATING]}
            variation={ButtonVariations.pinkPrimary}
          >
            Done
          </SubmitButton>
        </Root>
      </Fragment>
    );
  }
}

export default createForm()(RatingDescriptionForm);
